import {
  CustomHybridIcon,
  CustomIndicaIcon,
  CustomSativaIcon,
  Flex,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import type { WizardStepProps } from '../../../utils/wizard/types';
import { WizardStepKey } from '../../../utils/wizard/types';
import tileStyles from '../wizardOptionTile.module.css';
import stepStyles from '../wizardStep.module.css';
import styles from './lineageStep.module.css';

interface LineageData {
  description: string;
  icon?: ReactNode;
  label: string;
  value: string;
}

const LINEAGES: LineageData[] = [
  {
    description: 'Often chosen for more mellow, unwind-style vibes',
    icon: <CustomIndicaIcon size="md" color="grape-dark" mb={4} />,
    label: 'Indica-leaning',
    value: 'indica',
  },
  {
    description: 'Often chosen for brighter, more daytime-style vibes',
    icon: <CustomSativaIcon size="md" color="emerald-dark" mb={4} />,
    label: 'Sativa-leaning',
    value: 'sativa',
  },
  {
    description: 'A mix — many people pick this for a balanced feel',
    icon: <CustomHybridIcon size="md" color="grays-dark" mb={4} />,
    label: 'Hybrid',
    value: 'hybrid',
  },
  {
    description: "We'll match based on your other choices",
    label: 'No preference',
    value: 'no-preference',
  },
];

interface LineageOptionProps {
  description: string;
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  selected: boolean;
}

function LineageOption({
  description,
  icon,
  label,
  onClick,
  selected,
}: LineageOptionProps) {
  const className = clsx(
    tileStyles['tile'],
    stepStyles['option'],
    styles['option'],
    selected && tileStyles['selected']
  );

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-pressed={selected}
    >
      {icon}
      <Typography variant="body-bold">{label}</Typography>
      <Typography variant="mini" color="grays-dark" textAlign="center">
        {description}
      </Typography>
    </button>
  );
}

export function LineageStep({ form, updateForm }: WizardStepProps) {
  const selections = form[WizardStepKey.Lineage];

  const handleSelect = (value: string) => {
    const isSelected = selections.includes(value);
    updateForm({
      ...form,
      [WizardStepKey.Lineage]: isSelected ? [] : [value],
    });
  };

  return (
    <Flex flexDirection="column" alignItems="center" gap={32} width="100%">
      <Flex flexDirection="column" alignItems="center" gap={8}>
        <Typography variant="header-bold" textAlign="center">
          Do you have a strain preference?
        </Typography>
        <Typography variant="body" textAlign="center">
          Pick what you enjoy, or choose no preference.
        </Typography>
      </Flex>

      <div className={clsx(stepStyles['grid'], styles['grid'])}>
        {LINEAGES.map((lineage) => {
          const selected = selections.includes(lineage.value);

          return (
            <LineageOption
              key={lineage.value}
              description={lineage.description}
              icon={lineage.icon}
              label={lineage.label}
              onClick={() => handleSelect(lineage.value)}
              selected={selected}
            />
          );
        })}
      </div>
    </Flex>
  );
}
